import { Link } from "react-router-dom";
import { ScorePill, RiskPill, ROICell } from "@/components/opportunities/ScorePill";
import AsinStatusPill from "@/components/opportunities/AsinStatusPill";
import Sparkline from "@/components/opportunities/Sparkline";
import SetThumb from "@/components/opportunities/SetThumb";
import { ArrowSquareOut, WarningCircle, CheckCircle, XCircle } from "@phosphor-icons/react";
import { amazonUrl, bricklinkUrl } from "@/lib/links";

// Deals-style column registry: every column can be hidden via COLUMNS menu
// (SET is always shown). `sort` = server-side sort field.
export const BOARD_COLS = [
  { key: "name", label: "Name", align: "left", sort: "name" },
  { key: "asin", label: "ASIN", align: "left", sort: "asin" },
  { key: "theme", label: "Theme", align: "left", sort: "theme" },
  { key: "year", label: "Year", align: "right", sort: "year" },
  { key: "amz_bb", label: "AMZ Buy Box", align: "right", sort: "amazon.buy_box" },
  { key: "fba_fbm", label: "FBA / FBM", align: "right" },
  { key: "stock", label: "Stock", align: "center", sort: "amazon.in_stock" },
  { key: "rank", label: "Rank", align: "right", sort: "amazon.sales_rank" },
  { key: "ebay", label: "eBay Low", align: "right", sort: "ebay.lowest_new" },
  { key: "bl_lowest", label: "BL Lowest US", align: "right", sort: "bricklink.lowest_sealed_us" },
  { key: "bl_avg", label: "BL Avg", align: "right", sort: "bricklink.avg_sealed" },
  { key: "bl_sell", label: "BL Sellers", align: "right", sort: "bricklink.seller_count" },
  { key: "trend", label: "30D Trend", align: "center" },
  { key: "roi", label: "ROI %", align: "right", sort: "roi_pct" },
  { key: "net", label: "Net $", align: "right", sort: "net_profit" },
  { key: "score", label: "Score", align: "center", sort: "opportunity_score" },
  { key: "risk", label: "Risk", align: "center", sort: "risk_rating" },
  { key: "src", label: "Data Source", align: "center", sort: "data_source" },
  { key: "links", label: "Links", align: "center" },
];

const money = (n) => (n == null ? "--" : `$${Number(n).toFixed(2)}`);
const num = (n) => (n == null ? "--" : Number(n).toLocaleString());

function Th({ label, align, sortKey, sortBy, sortDir, onSort, testId }) {
  const active = sortKey && sortBy === sortKey;
  const cls = `px-3 py-2 text-[10px] uppercase tracking-wider font-bold whitespace-nowrap ${
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"
  } ${sortKey ? "cursor-pointer select-none hover:text-black" : ""} ${active ? "text-[#E3000B]" : "text-gray-500"}`;
  if (!sortKey) return <th className={cls}>{label}</th>;
  return (
    <th className={cls} onClick={() => onSort(sortKey)} data-testid={testId}
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"} title={`Sort by ${label.toLowerCase()}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[8px] ${active ? "opacity-100" : "opacity-30"}`}>{active && sortDir === "asc" ? "▲" : "▼"}</span>
      </span>
    </th>
  );
}

export default function OpportunityTable({ rows, sortBy, sortDir, onSort, loading, selected, onToggle, onToggleAll, isVisible }) {
  const allChecked = rows.length > 0 && rows.every(r => selected.has(r.set_number));
  const someChecked = rows.some(r => selected.has(r.set_number));
  const colCount = 2 + BOARD_COLS.filter(c => isVisible(c.key)).length;

  return (
    <div className="overflow-x-auto border border-[#E4E4E0] rounded-sm bg-[#FFFFFF]" data-testid="opportunity-table">
      <table className="w-full text-xs font-mono dense-table">
        <thead className="bg-[#F6F6F3] border-b border-[#E4E4E0] sticky top-0 z-10">
          <tr>
            <th className="px-2 py-2 w-8 text-center">
              <input type="checkbox" data-testid="board-select-all" aria-label="Select all rows on this page"
                checked={allChecked}
                ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                onChange={onToggleAll}
                className="accent-[#E3000B] w-3 h-3 cursor-pointer" />
            </th>
            <Th label="SET" align="left" sortKey="set_number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} testId="sort-set_number" />
            {BOARD_COLS.filter(c => isVisible(c.key)).map(c => (
              <Th key={c.key} label={c.label.toUpperCase()} align={c.align} sortKey={c.sort}
                  sortBy={sortBy} sortDir={sortDir} onSort={onSort} testId={`sort-${(c.sort || c.key).replace(/\./g, "-")}`} />
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={colCount} className="p-8 text-center text-gray-500" data-testid="table-loading">Loading opportunities…</td></tr>
          )}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={colCount} className="p-8 text-center text-gray-500" data-testid="table-empty">No opportunities match these filters.</td></tr>
          )}
          {!loading && rows.map(r => (
            <BoardRow key={r.set_number} r={r} checked={selected.has(r.set_number)} onToggle={() => onToggle(r.set_number)} isVisible={isVisible} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BoardRow({ r, checked, onToggle, isVisible }) {
  const amz = r.amazon || {};
  const bl = r.bricklink || {};
  const notListed = r.amazon_listed === false;
  const srcCls = r.data_source === "live" ? "text-[#00A850] border-[#00A850]/30 bg-[#00A850]/10"
    : r.data_source === "partial" ? "text-[#E3000B] border-[#E3000B]/30 bg-[#E3000B]/10" : "text-gray-500 border-gray-300 bg-gray-200/30";
  return (
    <tr className={`border-b border-[#F6F6F3] hover:bg-[#F6F6F3] transition-colors ${checked ? "bg-[#E3000B]/[0.04]" : ""}`} data-testid={`opp-row-${r.set_number}`}>
      <td className="px-2 py-2 text-center">
        <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`board-select-${r.set_number}`}
          aria-label={`Select ${r.set_number}`} className="accent-[#E3000B] w-3 h-3 cursor-pointer" />
      </td>
      <td className="px-3 py-2 text-left whitespace-nowrap">
        <Link to={`/set/${r.set_number}`} className="text-[#E3000B] hover:underline" data-testid={`opp-link-${r.set_number}`}>{r.set_number}</Link>
      </td>
      {isVisible("name") && (
        <td className="px-3 py-2 text-left">
          <div className="flex items-center gap-2">
            <SetThumb src={r.image_url} alt={r.name} size={28} />
            <span className="text-gray-900 truncate max-w-[180px]" title={r.name}>{r.name}</span>
            {r.retired && <span className="text-[9px] px-1 border border-gray-300 text-gray-600 rounded-sm">RET</span>}
            {r.has_archive_deal && (
              <span className="text-[9px] px-1.5 py-[1px] bg-[#00A850]/10 border border-[#00A850]/40 text-[#00A850] rounded-sm font-bold whitespace-nowrap"
                title={`You've bought this before — last buy cost ${r.last_buy_cost != null ? "$" + Number(r.last_buy_cost).toFixed(2) : "n/a"}`}
                data-testid={`prev-buy-${r.set_number}`}>
                PREV{r.last_buy_cost != null ? ` $${Number(r.last_buy_cost).toFixed(0)}` : ""}
              </span>
            )}
            {r.has_active_deal && !r.has_archive_deal && (
              <span className="text-[9px] px-1.5 py-[1px] bg-[#E3000B]/10 border border-[#E3000B]/40 text-[#E3000B] rounded-sm font-bold whitespace-nowrap"
                title="You have an active deal on this set" data-testid={`active-deal-${r.set_number}`}>ACTIVE</span>
            )}
          </div>
        </td>
      )}
      {isVisible("asin") && <td className="px-3 py-2 text-left"><AsinStatusPill row={r} testId={`source-${r.set_number}`} /></td>}
      {isVisible("theme") && <td className="px-3 py-2 text-left text-gray-600 truncate max-w-[120px]" title={r.theme}>{r.theme}</td>}
      {isVisible("year") && <td className="px-3 py-2 text-right text-gray-600">{r.year ?? "--"}</td>}
      {isVisible("amz_bb") && (
        <td className="px-3 py-2 text-right">
          {notListed ? (
            <span className="inline-flex items-center gap-1 text-[#FF3333]" title="Not listed on Amazon" data-testid={`not-on-amazon-${r.set_number}`}>
              <WarningCircle size={11} weight="fill" /> N/A
            </span>
          ) : amz.buy_box != null ? (
            <a href={amazonUrl(r.asin, r.set_number, r.asin_verified)} target="_blank" rel="noopener noreferrer"
               className="text-[#E3000B] hover:underline hover:text-[#D97706]" title={r.asin ? `Open ${r.asin} on Amazon` : "Search on Amazon"}>{money(amz.buy_box)}</a>
          ) : <span className="text-gray-400">--</span>}
        </td>
      )}
      {isVisible("fba_fbm") && (
        <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap">
          {amz.fba_buy_box != null && <span className="text-[#D97706]" title="FBA">{money(amz.fba_buy_box)}</span>}
          {amz.fba_buy_box != null && amz.fbm_buy_box != null && <span className="text-gray-400 mx-1">/</span>}
          {amz.fbm_buy_box != null && <span className="text-[#E3000B]" title="FBM">{money(amz.fbm_buy_box)}</span>}
          {amz.fba_buy_box == null && amz.fbm_buy_box == null && <span className="text-gray-400">--</span>}
        </td>
      )}
      {isVisible("stock") && (
        <td className="px-3 py-2 text-center">
          {amz.in_stock === true ? <CheckCircle size={14} weight="fill" className="text-[#00A850] inline" />
            : amz.in_stock === false ? <XCircle size={14} weight="fill" className="text-[#FF3333] inline" />
            : <span className="text-gray-400">--</span>}
        </td>
      )}
      {isVisible("rank") && <td className="px-3 py-2 text-right text-gray-800">{num(amz.sales_rank)}</td>}
      {isVisible("ebay") && (
        <td className="px-3 py-2 text-right" data-testid={`ebay-cell-${r.set_number}`}>
          {(r.ebay?.lowest_new != null) ? (
            <a href={r.ebay.listing_url || "#"} target="_blank" rel="noopener noreferrer"
               onClick={e => !r.ebay.listing_url && e.preventDefault()} title={r.ebay.listing_title || ""}
               className="text-[#0064D2] hover:underline">{money(r.ebay.lowest_new)}</a>
          ) : <span className="text-gray-400">--</span>}
        </td>
      )}
      {isVisible("bl_lowest") && (
        <td className="px-3 py-2 text-right">
          {bl.lowest_sealed_us != null ? (
            <a href={bricklinkUrl(r.set_number)} target="_blank" rel="noopener noreferrer" className="text-[#0055BF] hover:underline"
               title={`Verify on BrickLink · ${bl.seller_count || 0} US sellers · ${bl.qty_available || 0} qty`}>{money(bl.lowest_sealed_us)}</a>
          ) : <span className="text-gray-400">--</span>}
        </td>
      )}
      {isVisible("bl_avg") && <td className="px-3 py-2 text-right text-gray-800">{money(bl.avg_sealed)}</td>}
      {isVisible("bl_sell") && <td className="px-3 py-2 text-right text-gray-800">{bl.seller_count ?? "--"}</td>}
      {isVisible("trend") && (
        <td className="px-3 py-2">
          <div className="flex justify-center"><Sparkline data={amz.price_history || []} color={(r.roi_pct || 0) >= 0 ? "#00A850" : "#FF3333"} /></div>
        </td>
      )}
      {isVisible("roi") && <td className="px-3 py-2 text-right"><ROICell value={r.roi_pct} /></td>}
      {isVisible("net") && <td className="px-3 py-2 text-right text-gray-900">{money(r.net_profit)}</td>}
      {isVisible("score") && <td className="px-3 py-2 text-center"><ScorePill score={r.opportunity_score} testId={`score-${r.set_number}`} /></td>}
      {isVisible("risk") && <td className="px-3 py-2 text-center"><RiskPill risk={r.risk_rating} testId={`risk-${r.set_number}`} /></td>}
      {isVisible("src") && (
        <td className="px-3 py-2 text-center">
          <span className={`inline-flex items-center h-5 px-1.5 rounded-sm font-mono text-[10px] font-bold uppercase border ${srcCls}`}
                title={r.data_source === "live" ? "Real Amazon + BrickLink data" : r.data_source === "partial" ? "One side live" : "Demo data — scan to fetch real prices"}
                data-testid={`data-source-${r.set_number}`}>
            {r.data_source === "mock" || !r.data_source ? "DEMO" : r.data_source}
          </span>
        </td>
      )}
      {isVisible("links") && (
        <td className="px-3 py-2">
          <div className="flex items-center justify-center gap-1.5">
            <a href={amazonUrl(r.asin, r.set_number, r.asin_verified)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
               title={r.asin_verified ? "Open verified Amazon listing" : "Search on Amazon (ASIN not verified)"} data-testid={`amz-link-${r.set_number}`}
               className={`inline-flex items-center gap-0.5 px-1.5 h-5 border rounded-sm text-[9px] font-bold transition-colors ${
                 r.asin_verified ? "border-[#D97706]/60 text-[#D97706] hover:bg-[#D97706]/10" : "border-[#E4E4E0] text-[#D97706]/70 hover:border-[#D97706] hover:bg-[#D97706]/10"}`}>
              AMZ <ArrowSquareOut size={9} />
            </a>
            <a href={bricklinkUrl(r.set_number)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="Open on BrickLink" data-testid={`bl-link-${r.set_number}`}
               className="inline-flex items-center gap-0.5 px-1.5 h-5 border border-[#E4E4E0] rounded-sm text-[9px] font-bold text-[#0055BF] hover:border-[#0055BF] hover:bg-[#0055BF]/10 transition-colors">
              BL <ArrowSquareOut size={9} />
            </a>
          </div>
        </td>
      )}
    </tr>
  );
}
